import { Mtds } from 'prove-common'
import { Trans, useTranslation } from 'react-i18next'
import '../../i18n'
import { FetchError } from '../../index'
import ScanError from '../../ScanError'

export type ErrorMessageProps = {
  error?: unknown
  actionButton?: React.ReactNode
}

type ErrorView = {
  title: string
  content?: React.ReactNode
  color: 'danger' | 'info' | 'warning' | 'success'
  actionButton?: React.ReactNode
}

export const toErrorView = (
  error: unknown,
  t: ReturnType<typeof useTranslation>['t'],
  supportLink: React.ReactElement,
  actionButton?: React.ReactNode
): ErrorView => {
  const retry = (
    <Mtds.Button
      data-variant='primary'
      onClick={() => globalThis.location.reload()}
    >
      {t('retryButton')}
    </Mtds.Button>
  )

  const goBack = (
    <Mtds.Button
      data-variant='primary'
      onClick={() => globalThis.history.back()}
    >
      {t('goBackButton')}
    </Mtds.Button>
  )

  const fallback: ErrorView = {
    title: t('defaultTitle'),
    content: (
      <p>
        <Trans
          i18nKey='defaultContent'
          ns='errorMessage'
          components={{ supportLink }}
        />
      </p>
    ),
    color: 'danger',
    actionButton: actionButton ?? retry
  }

  const notFoundRoutePrefix = '404 Not Found: Error: No route matches URL'

  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return {
      title: t('networkTitle'),
      content: (
        <p>
          <Trans
            i18nKey='networkContent'
            ns='errorMessage'
            components={{ supportLink }}
          />
        </p>
      ),
      color: 'danger',
      actionButton: retry
    }
  }

  if (error instanceof FetchError) {
    const statusMessages: Record<number, Partial<ErrorView>> = {
      401: {
        title: t('authTitle'),
        content: <p>{t('authContent')}</p>,
        color: 'info',
        actionButton: (
          <Mtds.Button
            data-variant='primary'
            onClick={() => {
              globalThis.location.href = '/login?logout=true'
            }}
          >
            {t('goToLoginButton')}
          </Mtds.Button>
        )
      },
      403: {
        title: t('forbiddenTitle'),
        content: (
          <p>
            <Trans
              i18nKey='forbiddenContent'
              ns='errorMessage'
              components={{ supportLink }}
            />
          </p>
        ),
        color: 'info'
      },
      404: {
        title: t('notFoundTitle'),
        content: <p>{t('notFoundContent')}</p>,
        color: 'info'
      },
      409: {
        title: t('conflict409Title'),
        content: <p>{t('conflict409Content')}</p>,
        color: 'warning'
      },
      412: {
        title: t('conflictTitle'),
        content: <p>{t('conflictContent')}</p>,
        color: 'warning',
        actionButton: retry
      }
    }

    const status = typeof error.status === 'number' ? error.status : undefined
    const statusView =
      status === undefined ? {} : (statusMessages[status] ?? {})

    return {
      title:
        statusView.title ||
        t('defaultTitleWithStatus', { status: error.status }),
      content: statusView.content || fallback.content,
      color: statusView.color || fallback.color,
      actionButton: statusView.actionButton
    }
  }

  if (error instanceof ScanError) {
    return {
      title: t('cameraTitle'),
      content: <p>{t('cameraContent')}</p>,
      color: 'danger',
      actionButton: retry
    }
  }

  if (error instanceof Error && error.message.startsWith(notFoundRoutePrefix)) {
    return {
      title: t('routeNotFoundTitle'),
      content: (
        <p>
          {t('routeNotFoundContent', {
            path: globalThis.location?.pathname
          })}
        </p>
      ),
      color: 'info',
      actionButton: goBack
    }
  }

  if (error instanceof Error) {
    return {
      title: fallback.title,
      content: (
        <p>
          <Trans
            i18nKey='defaultContent'
            ns='errorMessage'
            components={{ supportLink }}
          />
        </p>
      ),
      color: fallback.color,
      actionButton: fallback.actionButton
    }
  }

  return fallback
}

export const ErrorMessage = ({ error, actionButton }: ErrorMessageProps) => {
  const { t } = useTranslation('errorMessage')
  const supportLink = (
    <a
      href='https://mattilsynet.pureservice.com/'
      target='_blank'
      rel='noopener noreferrer'
    >
      {t('supportLinkLabel')}
    </a>
  )
  const view = toErrorView(error, t, supportLink, actionButton)
  return (
    <Mtds.Alert data-color={view.color} as={Mtds.Prose}>
      <Mtds.Heading>{view.title}</Mtds.Heading>
      {view.content}
      {(actionButton ?? view.actionButton) && (
        <Mtds.Flex>{actionButton ?? view.actionButton}</Mtds.Flex>
      )}
    </Mtds.Alert>
  )
}
